/**
 * AreasTab - Manage the shared vocabulary of physical areas.
 *
 * Areas are the named places an inspection photo is tagged with — "Lobby",
 * "East Stairwell", "Loading Dock". The field UI picks from this list rather
 * than accepting free text, because free text made per-area reporting
 * impossible: "Mens Restroom", "Men's restroom" and "MENS RR" are three
 * different areas to any grouping.
 *
 * One list for every customer. It was per company until Oct 2026, which
 * meant re-entering "Lobby" for each one (beta feedback: "We do not need a
 * company option. It just needs to be generic areas we can add to any
 * inspection, with any client.").
 *
 * Editing is inline (each entry is just a name and an ordering), and areas are
 * deactivated rather than deleted by default: a retired area disappears from
 * the picker but stays attached to the historical photos that used it.
 */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Spinner } from '../../components/atoms';
import { areaService } from '../../services/areaService';
import type { Area } from '../../services/areaService';
import { FORM_INPUT_CLASS } from '../../utils/formClasses';

/* Shared input styling — the app-wide token, so this tab tracks the Admin and
   Buildings forms instead of holding its own copy of the string. */
const inputClass = FORM_INPUT_CLASS;

const AreasTab: React.FC = () => {
	const queryClient = useQueryClient();

	/** Error from a create/rename/deactivate action (the list has its own). */
	const [actionError, setActionError] = useState<string | null>(null);

	/** Inline "add area" form. */
	const [newAreaName, setNewAreaName] = useState('');

	/** Rename buffers, keyed by area id. */
	const [renameDraft, setRenameDraft] = useState<Record<number, string>>({});

	/* ---------------------------------------------------------------
	 * Data
	 * --------------------------------------------------------------- */

	/* All areas (active and retired) — the CMS is where retired ones are
	   managed, so unlike the field picker this list is not filtered. */
	const areasQuery = useQuery({
		queryKey: ['areas'],
		queryFn: () => areaService.list(),
	});
	const areas: Area[] = useMemo(() => areasQuery.data ?? [], [areasQuery.data]);

	const refresh = () => queryClient.invalidateQueries({ queryKey: ['areas'] });

	/* ---------------------------------------------------------------
	 * Mutations
	 * --------------------------------------------------------------- */

	const createMutation = useMutation({
		mutationFn: (data: { name: string }) => areaService.create({ name: data.name }),
		onSuccess: () => {
			setNewAreaName('');
			refresh();
		},
		onError: (err: any) => setActionError(err?.data?.message ?? 'Could not create that area.'),
	});

	const updateMutation = useMutation({
		mutationFn: ({ id, data }: { id: number; data: { name?: string; is_active?: boolean } }) =>
			areaService.update(id, data),
		onSuccess: refresh,
		onError: (err: any) => setActionError(err?.data?.message ?? 'Could not update that area.'),
	});

	const deleteMutation = useMutation({
		mutationFn: (id: number) => areaService.remove(id),
		onSuccess: refresh,
		onError: (err: any) => setActionError(err?.data?.message ?? 'Could not delete that area.'),
	});

	const handleCreate = (e: React.FormEvent) => {
		e.preventDefault();
		setActionError(null);
		const name = newAreaName.trim();
		if (!name) return;
		createMutation.mutate({ name });
	};

	/** Commit a rename only when the value actually changed. */
	const handleRename = (area: Area) => {
		const next = (renameDraft[area.id] ?? '').trim();
		if (!next || next === area.name) {
			setRenameDraft((prev) => {
				const { [area.id]: _drop, ...rest } = prev;
				return rest;
			});
			return;
		}
		updateMutation.mutate({ id: area.id, data: { name: next } });
		setRenameDraft((prev) => {
			const { [area.id]: _drop, ...rest } = prev;
			return rest;
		});
	};

	const handleDelete = (area: Area) => {
		if (!window.confirm(`Delete "${area.name}"? Photos already tagged with it keep their record but lose the tag. Deactivating instead keeps the history intact.`)) return;
		setActionError(null);
		deleteMutation.mutate(area.id);
	};

	const error = actionError ?? (areasQuery.error ? 'Could not load areas.' : null);

	/* ---------------------------------------------------------------
	 * Render
	 * --------------------------------------------------------------- */

	return (
		<div className="areas-tab" data-ref="organism-areas-tab">
			<div className="areas-tab__intro mb-16px">
				<h2 className="text-18px font-heading font-semibold text-brand-heading mb-4px">Areas</h2>
				<p className="text-14px font-sans text-brand-text-muted">
					The areas an inspector can tag a photo with — one list, offered on every inspection for every
					customer. Keeping this a picked list rather than free text is what makes per-area reporting possible.
				</p>
			</div>

			{error && <Alert variant="error" className="mb-16px">{error}</Alert>}

			{/* --- Add an area --- */}
			<form onSubmit={handleCreate} className="areas-tab__create flex flex-col tablet:flex-row gap-8px mb-24px p-12px border border-dashed border-brand-border rounded-md bg-white">
				<div className="flex-1">
					<label htmlFor="new-area-name" className="sr-only">New area name</label>
					<input
						id="new-area-name"
						type="text"
						value={newAreaName}
						onChange={(e) => setNewAreaName(e.target.value)}
						placeholder="New area — e.g. East Stairwell"
						className={inputClass}
						maxLength={100}
					/>
				</div>
				<Button type="submit" variant="primary" size="sm" disabled={createMutation.isPending || !newAreaName.trim()}>
					{createMutation.isPending ? 'Adding…' : 'Add area'}
				</Button>
			</form>

			{/* --- The vocabulary --- */}
			{areasQuery.isLoading ? (
				<div className="flex items-center justify-center py-32px"><Spinner size="md" label="Loading areas" /></div>
			) : areas.length === 0 ? (
				<p className="text-14px font-sans text-brand-text-muted text-center py-32px border border-dashed border-brand-border rounded-md">
					No areas configured yet. Add the first one above — inspectors will then pick from this list instead of typing.
				</p>
			) : (
				<section className="areas-tab__group mb-24px">
					<ul className="areas-tab__list border border-brand-border rounded-md bg-white divide-y divide-brand-border">
						{areas.map((area) => (
							<li key={area.id} className={`areas-tab__item flex flex-col tablet:flex-row tablet:items-center gap-8px p-12px ${area.isActive ? '' : 'areas-tab__item--inactive bg-brand-surface'}`}>
								<input
									type="text"
									value={renameDraft[area.id] ?? area.name}
									onChange={(e) => setRenameDraft((prev) => ({ ...prev, [area.id]: e.target.value }))}
									onBlur={() => handleRename(area)}
									onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); } }}
									aria-label={`Rename ${area.name}`}
									className={`${inputClass} flex-1`}
									maxLength={100}
								/>

								<div className="flex items-center gap-8px">
									{!area.isActive && (
										<span className="text-11px font-sans font-semibold uppercase tracking-wide text-brand-text-muted bg-gray-100 border border-gray-300 rounded-full px-8px py-2px">
											Retired
										</span>
									)}
									{/* Deactivate is the safe default — the area leaves the
									    picker while historical photos keep their tag. */}
									<Button
										variant="secondary"
										size="sm"
										onClick={() => updateMutation.mutate({ id: area.id, data: { is_active: !area.isActive } })}
										disabled={updateMutation.isPending}
									>
										{area.isActive ? 'Retire' : 'Reactivate'}
									</Button>
									<Button
										variant="danger"
										size="sm"
										onClick={() => handleDelete(area)}
										disabled={deleteMutation.isPending}
									>
										Delete
									</Button>
								</div>
							</li>
						))}
					</ul>
				</section>
			)}
		</div>
	);
};

export default AreasTab;
